import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { generateRandomArray } from '../utils/arrayUtils';

const SearchingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ArrayContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
`;

const ArrayTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const ArrayVisualization = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-height: 400px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  position: relative;
`;

const ArrayBar = styled.div`
  background: ${props => {
    if (props.isCurrent) return '#ff6b6b';
    if (props.isFound) return '#51cf66';
    if (props.isChecked) return '#ffd43b';
    return '#f8f9fa';
  }};
  width: ${props => Math.max(20, 800 / props.arrayLength)}px;
  height: ${props => props.value * 3}px;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const BarValue = styled.div`
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${ArrayBar}:hover & {
    opacity: 1;
  }
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 14px;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.color};
`;

const Controls = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
`;

const ControlGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  background: ${props => props.primary ? '#51cf66' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-right: 10px;
  margin-bottom: 10px;
  
  &:hover:not(:disabled) {
    background: ${props => props.primary ? '#40c057' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AlgorithmInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  color: white;
  height: fit-content;
`;

const InfoTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  text-align: center;
`;

const InfoText = styled.p`
  margin: 0 0 15px 0;
  line-height: 1.6;
  opacity: 0.9;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

function SearchingPage() {
  const [array, setArray] = useState([]);
  const [arraySize, setArraySize] = useState(15);
  const [searchValue, setSearchValue] = useState('');
  const [currentAlgorithm, setCurrentAlgorithm] = useState('linear');
  const [isSearching, setIsSearching] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [foundIndex, setFoundIndex] = useState(-1);
  const [checkedIndices, setCheckedIndices] = useState([]);
  const [comparisons, setComparisons] = useState(0);
  const [searchSpeed, setSearchSpeed] = useState(500);

  const generateNewArray = useCallback(() => {
    const newArray = generateRandomArray(arraySize, 5, 100);
    setArray(newArray);
    resetSearch();
  }, [arraySize]);

  useEffect(() => {
    generateNewArray();
  }, [generateNewArray]);

  const resetSearch = () => {
    setCurrentIndex(-1);
    setFoundIndex(-1);
    setCheckedIndices([]);
    setComparisons(0);
    setIsSearching(false);
  };

  const linearSearch = async () => {
    if (isSearching || !searchValue) return;
    
    setIsSearching(true);
    setComparisons(0);
    setCheckedIndices([]);
    setFoundIndex(-1);
    
    for (let i = 0; i < array.length; i++) {
      setCurrentIndex(i);
      setComparisons(prev => prev + 1);
      setCheckedIndices(prev => [...prev, i]);
      
      await new Promise(resolve => setTimeout(resolve, 1000 / searchSpeed));
      
      if (array[i] === parseInt(searchValue)) {
        setFoundIndex(i);
        break;
      }
    }
    
    setIsSearching(false);
  };

  const binarySearch = async () => {
    if (isSearching || !searchValue) return;
    
    setIsSearching(true);
    setComparisons(0);
    setCheckedIndices([]);
    setFoundIndex(-1);
    
    // Sort array for binary search
    const sortedArray = [...array].sort((a, b) => a - b);
    setArray(sortedArray);
    
    let left = 0;
    let right = sortedArray.length - 1;
    
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      setCurrentIndex(mid);
      setComparisons(prev => prev + 1);
      setCheckedIndices(prev => [...prev, mid]);
      
      await new Promise(resolve => setTimeout(resolve, 1000 / searchSpeed));
      
      if (sortedArray[mid] === parseInt(searchValue)) {
        setFoundIndex(mid);
        break;
      } else if (sortedArray[mid] < parseInt(searchValue)) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }
    
    setIsSearching(false);
  };

  const handleSearch = async () => {
    if (currentAlgorithm === 'linear') {
      await linearSearch();
    } else {
      await binarySearch();
    }
  };

  return (
    <SearchingContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Searching Algorithm Visualizer</Title>
        <Subtitle>Learn and visualize different searching algorithms with interactive animations</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <ArrayContainer>
            <ArrayTitle>Array Visualization</ArrayTitle>
            <ArrayVisualization>
              {array.map((value, index) => (
                <ArrayBar
                  key={index}
                  value={value}
                  arrayLength={array.length}
                  isCurrent={index === currentIndex}
                  isFound={index === foundIndex}
                  isChecked={checkedIndices.includes(index)}
                >
                  <BarValue>{value}</BarValue>
                </ArrayBar>
              ))}
            </ArrayVisualization>
            
            <Legend>
              <LegendItem>
                <LegendColor color="#f8f9fa" />
                <span>Unchecked</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ffd43b" />
                <span>Checked</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#ff6b6b" />
                <span>Current</span>
              </LegendItem>
              <LegendItem>
                <LegendColor color="#51cf66" />
                <span>Found</span>
              </LegendItem>
            </Legend>
          </ArrayContainer>
          
          <Controls>
            <ControlGroup>
              <Label>Array Size:</Label>
              <Input
                type="range"
                min="5"
                max="30"
                value={arraySize}
                onChange={(e) => setArraySize(parseInt(e.target.value))}
                disabled={isSearching}
              />
              <div style={{ textAlign: 'center', marginTop: '5px' }}>{arraySize}</div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Search Value:</Label>
              <Input
                type="number"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Enter a number to search"
                disabled={isSearching}
              />
            </ControlGroup>
            
            <ControlGroup>
              <Label>Search Speed:</Label>
              <Input
                type="range"
                min="100"
                max="1000"
                value={searchSpeed}
                onChange={(e) => setSearchSpeed(parseInt(e.target.value))}
                disabled={isSearching}
              />
              <div style={{ textAlign: 'center', marginTop: '5px' }}>{searchSpeed}ms</div>
            </ControlGroup>
            
            <ControlGroup>
              <Label>Algorithm:</Label>
              <div>
                <Button
                  primary={currentAlgorithm === 'linear'}
                  onClick={() => setCurrentAlgorithm('linear')}
                  disabled={isSearching}
                >
                  Linear Search
                </Button>
                <Button
                  primary={currentAlgorithm === 'binary'}
                  onClick={() => setCurrentAlgorithm('binary')}
                  disabled={isSearching}
                >
                  Binary Search
                </Button>
              </div>
            </ControlGroup>
            
            <ControlGroup>
              <Button onClick={handleSearch} disabled={isSearching || !searchValue}>
                {isSearching ? 'Searching...' : 'Start Search'}
              </Button>
              <Button onClick={generateNewArray} disabled={isSearching}>
                Generate New Array
              </Button>
            </ControlGroup>
          </Controls>
        </LeftPanel>
        
        <AlgorithmInfo>
          <InfoTitle>Algorithm Information</InfoTitle>
          
          <InfoText>
            <strong>Current Algorithm:</strong> {currentAlgorithm === 'linear' ? 'Linear Search' : 'Binary Search'}
          </InfoText>
          
          {currentAlgorithm === 'linear' ? (
            <InfoText>
              <strong>Linear Search:</strong> Sequentially checks each element in the array until the target value is found or the end is reached.
            </InfoText>
          ) : (
            <InfoText>
              <strong>Binary Search:</strong> Efficiently finds the target value by repeatedly dividing the search interval in half. Requires a sorted array.
            </InfoText>
          )}
          
          <StatsContainer>
            <StatBox>
              <StatValue>{comparisons}</StatValue>
              <StatLabel>Comparisons</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{foundIndex !== -1 ? foundIndex : 'Not Found'}</StatValue>
              <StatLabel>Result Index</StatLabel>
            </StatBox>
          </StatsContainer>
          
          {foundIndex !== -1 && (
            <InfoText style={{ marginTop: '20px', textAlign: 'center', color: '#51cf66' }}>
              ✓ Value found at index {foundIndex}!
            </InfoText>
          )}
        </AlgorithmInfo>
      </MainContent>
    </SearchingContainer>
  );
}

export default SearchingPage;
